// Resolved runtime configuration for the dashboard.
//
// In production (Cloudflare Pages) the dashboard talks to the gateway
// same-origin under `/api`, which is proxied server-side by the Pages
// Function at functions/api/[[path]].js. That Function injects the
// `GATEWAY_API_KEY` Pages secret as a Bearer header so the key never reaches
// the browser bundle. The frontend therefore carries NO API key and never
// references one.
//
// For local development against a remote gateway, set
// `VITE_GATEWAY_BASE_URL` to the gateway origin (e.g.
// `https://llm-gateway.overlink.top`) and configure the Vite dev-server
// `/api` proxy (see vite.config.ts). This is an explicit local override
// only; it is never used in the production build.

/** Base URL prefix for all gateway /api requests. Defaults to same-origin `/api`. */
export const GATEWAY_API_BASE: string = resolveApiBase()

/** The upstream gateway origin used by the Vite dev proxy (local only). */
export const GATEWAY_ORIGIN: string | null = resolveGatewayOrigin()

function resolveApiBase(): string {
  // Local-dev explicit override: an origin whose `/api` should be used
  // directly (bypassing the same-origin proxy). Must end with `/api`.
  const override = ((import.meta.env.VITE_GATEWAY_BASE_URL as string | undefined) ?? '').trim()
  if (override) {
    const normalized = override.replace(/\/+$/, '')
    return normalized.endsWith('/api') ? normalized : `${normalized}/api`
  }
  return '/api'
}

function resolveGatewayOrigin(): string | null {
  const override = ((import.meta.env.VITE_GATEWAY_BASE_URL as string | undefined) ?? '').trim()
  if (!override) return null
  return override.replace(/\/+$/, '').replace(/\/api$/, '')
}

// Poll cadences. Health is status-y and cheap to poll faster; aggregates run
// a D1 scan so we poll them more conservatively. Both are overridable via env
// for ops tuning without touching code.
export const HEALTH_REFETCH_MS = Number(import.meta.env.VITE_HEALTH_REFETCH_MS ?? 5000)
export const AGGREGATES_REFETCH_MS = Number(import.meta.env.VITE_AGGREGATES_REFETCH_MS ?? 15000)
export const EVENTS_REFETCH_MS = Number(import.meta.env.VITE_EVENTS_REFETCH_MS ?? 30000)

// Hard cap on the number of recent events fetched for the events panel.
export const EVENTS_LIMIT = Number(import.meta.env.VITE_EVENTS_LIMIT ?? 50)
